import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AuthenticationContext } from '@vs-online-only-app/state';
import { CenteredCardView } from '@vs-online-only-app/ui';
import ResetPasswordStep1 from './steps/ResetPasswordStep1';
import ResetPasswordStep2 from './steps/ResetPasswordStep2';
import ResetPasswordStep3 from './steps/ResetPasswordStep3';
import EmailLink from './EmailLink';

const ResetPasswordView: FC = () => {
  const [token, setToken] = useState('');
  const [searchParams, setQueryParams] = useSearchParams();
  const [email, setEmail] = useState(
    decodeURIComponent(searchParams.get('email') || ''),
  );

  const codeFromQuery = decodeURIComponent(searchParams.get('code') || '');

  const [step, setStep] = useState(email && codeFromQuery ? 2 : 1);

  const { login, isAuthenticated } = useContext(AuthenticationContext);

  const step1NextStep = useCallback(() => {
    setQueryParams({ email });
    setStep(2);
  }, [email, setQueryParams]);

  const step2NextStep = useCallback((token: string) => {
    setToken(token);
    setStep(3);
  }, []);

  const step3NextStep = useCallback(
    async (updatedPassword: string) => {
      try {
        await login({
          email,
          password: updatedPassword,
        });
      } catch (_e) {
        console.error(_e);
      }
    },
    [email, login],
  );

  const body = useMemo(() => {
    // Info with CTAs: Cancel or Send Code request
    if (step === 1) {
      return (
        <ResetPasswordStep1
          email={email}
          setEmail={setEmail}
          nextStep={step1NextStep}
        />
      );
    }

    // Input field for token to validate and CTAs: Cancel or Validate token Submit
    if (step === 2) {
      return (
        <ResetPasswordStep2
          email={email}
          nextStep={step2NextStep}
          previousStep={() => setStep(1)}
          code={codeFromQuery}
        />
      );
    }

    // Input fields for creating new password and CTAs: Cancel or Create Password Submit
    if (step === 3) {
      return (
        <ResetPasswordStep3
          email={email}
          token={token}
          nextStep={step3NextStep}
          previousStep={() => setStep(2)}
        />
      );
    }

    return null;
  }, [
    codeFromQuery,
    email,
    step,
    step1NextStep,
    step2NextStep,
    step3NextStep,
    token,
  ]);

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <CenteredCardView
      body={body}
      footer={
        <EmailLink email={email} to="/login">
          Back to login
        </EmailLink>
      }
    />
  );
};

export default ResetPasswordView;
